import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import '../style/AdminStaffAvailability.css';

const toLocalDateString = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const getMonthDateRange = (date) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    startDate: startOfMonth.toISOString().split('T')[0],
    endDate: endOfMonth.toISOString().split('T')[0],
  };
};

// Read-only mirror of Health Hub's Admin > Clinic Closure Manager
// (AdminBlockedDates.js), which is where whole-clinic closures — holidays,
// staff training days, etc. — are actually logged. RIS and Health Hub share
// one database, so this just reads clinic_blocked_dates via
// GET /scheduling/clinicClosures; there's no write UI here because the
// clinic's own closure calendar is Health Hub's to manage, and
// checkBookingAvailability() (routes/scheduling.js) already enforces these
// dates at booking time regardless of which app someone tries to book from.
export default function ClinicClosures({ BASE_URL }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [closures, setClosures] = useState([]); // { id, blocked_date, reason }[]

  const fetchClosures = useCallback(async () => {
    const { startDate, endDate } = getMonthDateRange(currentDate);
    try {
      const response = await fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`);
      if (response.ok) {
        const data = await response.json();
        setClosures(data);
      }
    } catch (err) {
      logger.error('Error fetching clinic closures:', err);
    }
  }, [BASE_URL, currentDate]);

  useEffect(() => {
    fetchClosures();
  }, [fetchClosures]);

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const closuresByDate = {};
  closures.forEach(c => {
    closuresByDate[c.blocked_date.split('T')[0]] = c;
  });

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="avail-calendar-day" style={{ visibility: 'hidden' }} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    const closure = closuresByDate[dateStr];
    const isPast = new Date(dateStr) < new Date(new Date().toDateString());
    days.push(
      <div
        key={day}
        className={`avail-calendar-day ${closure ? 'full-blocked' : ''} ${isPast ? 'past' : ''}`}
        title={closure ? (closure.reason ? `Clinic closed — ${closure.reason}` : 'Clinic closed') : 'Open'}
      >
        <span className="avail-day-number">{day}</span>
        {closure && <span className="avail-day-badge full">Closed</span>}
      </div>
    );
  }

  const upcomingClosures = closures
    .filter(c => new Date(c.blocked_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.blocked_date) - new Date(b.blocked_date));

  return (
    <div className="avail-container">
      <h1 className="avail-title">Clinic Closures</h1>
      <p className="avail-subtitle">
        Whole-clinic closures — holidays, staff training days, etc. — carried over from Health Hub's Admin &gt; Clinic
        Closure Manager. The clinic is closed to every visit type on these dates; this view is read-only here since
        Health Hub is where they're logged. A closure listed here already blocks booking on the Schedule calendar and
        in every scheduling popup.
      </p>

      <div className="avail-calendar-header">
        <button className="avail-nav-btn" onClick={prevMonth}>{'<'}</button>
        <h2>{currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}</h2>
        <button className="avail-nav-btn" onClick={nextMonth}>{'>'}</button>
      </div>

      <div className="avail-calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="avail-calendar-day-header">{d}</div>
        ))}
        {days}
      </div>

      {upcomingClosures.length > 0 && (
        <div className="avail-upcoming-section">
          <h3>Upcoming Closures</h3>
          <ul className="avail-upcoming-list">
            {upcomingClosures.map(c => {
              const [year, month, day] = c.blocked_date.split('T')[0].split('-');
              const display = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))
                .toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
              return (
                <li key={c.id} className="avail-upcoming-item">
                  <span className="avail-upcoming-date">{display}</span>
                  {c.reason && <span className="avail-upcoming-reason">{c.reason}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
